import { AxiosResponse } from "axios";
import { useCallback, useEffect, useState } from "react";

import { useAxiosClient } from "@/hooks";
import { SendStudyMessageRequest } from "@/types/requests";
import { ConversationMessage, Persona } from "@/types/resources";
import {
  GetStudyConversationResponse,
  SendStudyConversationMessageResponse,
} from "@/types/responses";

const useGetConversation = (
  studyId: string,
  runId: string,
  personaId: string
) => {
  const [conversation, setConversation] = useState<ConversationMessage[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [persona, setPersona] = useState<Persona | null>(null);
  const [translatedConversation, setTranslatedConversation] = useState<
    ConversationMessage[]
  >([]);

  const client = useAxiosClient(
    `/studies/${studyId}/runs/${runId}/conversations/${personaId}`
  );

  const getConversation = useCallback(() => {
    client
      .get<GetStudyConversationResponse>("")
      .then((response) => {
        setConversation(response.data.conversations.conversation);
        setPersona(response.data.conversations.persona);
        setTranslatedConversation(
          response.data.conversations.translated_conversation
        );
        setError(null);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [client]);

  // Clear stale data and re-enter the loading state during render (not in an
  // effect) when the conversation identity changes. The initial mount needs no
  // reset since the state already starts empty/loading.
  const inputsKey = `${studyId}|${runId}|${personaId}`;
  const [prevInputsKey, setPrevInputsKey] = useState(inputsKey);
  if (prevInputsKey !== inputsKey) {
    setPrevInputsKey(inputsKey);
    setConversation([]);
    setPersona(null);
    setTranslatedConversation([]);
    setLoading(true);
    setError(null);
  }

  const refresh = useCallback(() => {
    setLoading(true);
    setError(null);
    getConversation();
  }, [getConversation]);

  useEffect(() => {
    getConversation();
  }, [getConversation]);

  return {
    conversation,
    error,
    getConversation: refresh,
    loading,
    persona,
    translatedConversation,
  };
};

const useSendConversationMessage = (
  studyId: string,
  runId: string,
  personaId: string
) => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const client = useAxiosClient(
    `/studies/${studyId}/runs/${runId}/conversations/${personaId}`
  );

  const sendMessage = useCallback(
    async (data: SendStudyMessageRequest) => {
      setError(null);
      setLoading(true);

      try {
        const response = await client.post<
          SendStudyConversationMessageResponse,
          AxiosResponse<SendStudyConversationMessageResponse>,
          SendStudyMessageRequest
        >("", data);
        return {
          conversation: response.data.conversations.conversation,
          translated_conversation:
            response.data.conversations.translated_conversation,
        };
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [client]
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    clearError,
    error,
    loading,
    sendMessage,
  };
};

export { useGetConversation, useSendConversationMessage };
